/**
 * Referral Query Hooks
 * Handles referral code, stats, and history
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api.js';
import { queryKeys } from '../../lib/queryKeys.js';

/**
 * Get all referral data in a single query
 * Combines code, stats, and history for efficiency
 */
export function useReferralData(options = {}) {
    return useQuery({
        queryKey: queryKeys.referralData,
        queryFn: async () => {
            const [codeResponse, statsResponse, historyResponse] = await Promise.all([
                api.getReferralCode().catch(() => null),
                api.getReferralStats().catch(() => null),
                api.getReferralHistory(10).catch(() => null),
            ]);

            return {
                code: codeResponse,
                stats: statsResponse,
                history: historyResponse,
            };
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        ...options,
    });
}

/**
 * Get referral code only
 */
export function useReferralCode(options = {}) {
    return useQuery({
        queryKey: queryKeys.referralCode,
        queryFn: async () => {
            const response = await api.getReferralCode();
            return response;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        ...options,
    });
}

/**
 * Get referral stats
 */
export function useReferralStats(options = {}) {
    return useQuery({
        queryKey: queryKeys.referralStats,
        queryFn: async () => {
            const response = await api.getReferralStats();
            return response;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        ...options,
    });
}

/**
 * Get referral history
 */
export function useReferralHistory(limit = 10, options = {}) {
    return useQuery({
        queryKey: queryKeys.referralHistory(limit),
        queryFn: async () => {
            const response = await api.getReferralHistory(limit);
            return response;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        ...options,
    });
}

/**
 * Mutation: Regenerate referral code
 */
export function useRegenerateReferralCode() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const response = await api.regenerateReferralCode();
            return response;
        },
        onSuccess: () => {
            // Invalidate referral queries to refetch
            queryClient.invalidateQueries({ queryKey: queryKeys.referralData });
            queryClient.invalidateQueries({ queryKey: queryKeys.referralCode });
        },
    });
}

/**
 * Mutation: Apply referral code
 */
export function useApplyReferralCode() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (referralCode) => {
            const response = await api.applyReferralCode(referralCode);
            return response;
        },
        onSuccess: () => {
            // Invalidate credit queries since applying referral gives bonus credits
            queryClient.invalidateQueries({ queryKey: ['credits'] });
            queryClient.invalidateQueries({ queryKey: queryKeys.referralData });
        },
    });
}

export default {
    useReferralData,
    useReferralCode,
    useReferralStats,
    useReferralHistory,
    useRegenerateReferralCode,
    useApplyReferralCode,
};
